import {useCallback, useEffect, useRef} from 'react';
import Analytics from '../utils/Analytics';
import Environment from '../utils/Environment';
import {PAYWALL_EVENTS, PaywallSlug} from '../analytics/paywallEvents';

type PaywallDismissTrackingOptions = {
  /** Identifies which paywall these events came from, e.g. "email-confirmation-checkout". */
  paywall: PaywallSlug;
  /**
   * Extra properties to attach to the events, read at leave-time. Pass current values on
   * every render — the hook snapshots the latest into a ref so the listeners can attach
   * once without re-binding.
   */
  properties?: Record<string, unknown>;
};

/**
 * Generic paywall dismiss / dwell instrumentation, reusable across any paywall screen.
 *
 * Link-out and full-screen paywalls generally have no dismiss button, so the users who
 * leave without converting are only observable via the browser page-lifecycle. This hook
 * fires:
 *
 *  - `[Web Payments] Paywall Dismissed` once, when the page is first hidden (`visibilitychange` -> hidden)
 *    or on `pagehide` — the mobile-reliable leave events (`beforeunload`/`unload` do not
 *    fire reliably on iOS Safari). Sent via `Analytics.trackOnLeave` (sendBeacon) so it's
 *    delivered even as the page is discarded; a normal `track()` XHR would be dropped.
 *    `dwellMs` (time on page) is stamped on every event by `Analytics.track`, so it rides
 *    along automatically.
 *  - `[Web Payments] Paywall Refocused` when the page becomes visible again, with `awayMs`. Because the
 *    dismiss beacon must fire on the first `hidden` (the last reliable send before a
 *    possible tab-close), it can't be retroactively tagged with whether the user returned.
 *    So "left but returned" = fired Dismissed AND then Refocused; a terminal leave =
 *    Dismissed with no following Refocused.
 *
 * Both events include the `paywall` prop plus whatever is passed in `properties`. Call the
 * returned `markPurchased()` on a successful purchase so the post-conversion navigation
 * isn't miscounted as a dismiss.
 */
export default function usePaywallDismissTracking({paywall, properties}: PaywallDismissTrackingOptions) {
  const purchasedRef = useRef(false);
  const dismissTrackedRef = useRef(false);
  const hiddenAtRef = useRef<number | null>(null);

  // Snapshot the latest paywall + properties each render so the listeners below attach
  // once and still read current values at leave-time.
  const paywallRef = useRef(paywall);
  paywallRef.current = paywall;
  const propertiesRef = useRef(properties);
  propertiesRef.current = properties;

  useEffect(() => {
    const buildProps = (extra: Record<string, unknown>) => ({
      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
      paywall: paywallRef.current,
      ...propertiesRef.current,
      ...extra,
    });
    const trackDismiss = () => {
      if (dismissTrackedRef.current || purchasedRef.current) return;
      dismissTrackedRef.current = true;
      // Fires as the page is unloading/hidden — use the beacon transport so the browser
      // still delivers it after the page is gone (a normal track() XHR would be dropped).
      Analytics.trackOnLeave(PAYWALL_EVENTS.dismissed, buildProps({}));
    };
    const trackRefocus = (awayMs: number) => {
      if (purchasedRef.current) return;
      // The page is provably alive here (it just became visible), so a normal track() is fine.
      Analytics.track(PAYWALL_EVENTS.refocused, buildProps({awayMs}));
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Math.round(performance.now());
        trackDismiss();
      } else if (document.visibilityState === 'visible' && hiddenAtRef.current !== null) {
        const awayMs = Math.round(performance.now()) - hiddenAtRef.current;
        hiddenAtRef.current = null;
        trackRefocus(awayMs);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', trackDismiss);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', trackDismiss);
    };
  }, []);

  const markPurchased = useCallback(() => {
    purchasedRef.current = true;
  }, []);

  return {markPurchased};
}
